import { useQueryClient, useQuery, skipToken } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { IApplicationData, IBudgetLand } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from './applicationQueries';

/**
 * Reads and writes a single field on the Land Budget form in the React Query cache.
 * Finds the form by type === 'Land Budget' and propertyGuid.
 */
export function useBudgetLandField<T>(
    propertyGuid: string,
    selector: (budget: IBudgetLand) => T,
    updater: (budget: IBudgetLand, value: T) => IBudgetLand
): [T | undefined, (value: T) => void] {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const queryClient = useQueryClient();

    const { data: currentValue } = useQuery<IApplicationData, Error, T | undefined>({
        queryKey: applicationQueryKey(entryGuid ?? ''),
        queryFn: skipToken,
        select: (d) => {
            const form = d.information?.properties?.forms?.find(
                (f) => f.type === 'Land Budget' && f.propertyGuid === propertyGuid
            ) as IBudgetLand | undefined;
            return form !== undefined ? selector(form) : undefined;
        },
    });

    const setValue = (value: T) => {
        if (entryGuid === undefined) return;
        queryClient.setQueryData<IApplicationData>(applicationQueryKey(entryGuid), (old) => {
            if (!old) return old;
            const forms = old.information?.properties?.forms ?? [];
            const idx = forms.findIndex(
                (f) => f.type === 'Land Budget' && f.propertyGuid === propertyGuid
            );
            if (idx === -1) return old;
            const form = forms[idx] as IBudgetLand;
            const newForms = [...forms];
            newForms[idx] = updater(form, value);
            return {
                ...old,
                information: {
                    ...old.information,
                    properties: {
                        ...old.information.properties,
                        forms: newForms,
                    },
                },
            };
        });
    };

    return [currentValue, setValue];
}
